import { useId, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ListTree, Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { ApiError } from '../../lib/api';
import {
  composicionApi,
  composicionKeys,
  type ArbolComposicion,
  type HerramientaCatalogo,
  type TipoFase,
} from '../../lib/composicion';
import { ArcoFamilias, contarFamilias } from './ArcoFamilias';
import { ColumnaFase, TIPO_FASE_INFO } from './ColumnaFase';
import type { OpcionSesion } from './FichaActividad';

const TIPOS_FASE: TipoFase[] = ['divergente', 'convergente', 'neutra'];

/**
 * Lienzo central del compositor: fases como columnas con scroll horizontal,
 * cada una con sus sesiones apiladas y su agenda vertical de actividades.
 * Al final del lienzo vive «+ Fase»; sin fases, un CTA grande que enseña.
 */
export function LienzoItinerario({
  arbol,
  procesoId,
  herramientaSeleccionada,
}: {
  arbol: ArbolComposicion;
  procesoId: string;
  herramientaSeleccionada: HerramientaCatalogo | null;
}) {
  // Sesiones del árbol completo, aplanadas: destinos de «mover a otra sesión».
  const opcionesSesion = useMemo<OpcionSesion[]>(() => {
    const opciones: OpcionSesion[] = [];
    for (const fase of arbol.fases) {
      fase.sesiones.forEach((s, i) => {
        opciones.push({
          id: s.id,
          etiqueta: `${fase.nombre} · Sesión ${i + 1}${s.fecha ? ` (${s.fecha})` : ''}`,
          totalActividades: s.actividades.length,
        });
      });
    }
    return opciones;
  }, [arbol.fases]);

  // Silueta del método para el ArcoFamilias (pedagogía sobre el lienzo).
  const conteoFamilias = useMemo(() => contarFamilias(arbol), [arbol]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ArcoFamilias conteo={conteoFamilias} />

      {arbol.fases.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-10 text-center">
          <ListTree className="h-12 w-12 text-content-muted" aria-hidden="true" />
          <h2 className="font-display text-xl font-semibold text-content">
            Este proceso todavía no tiene fases
          </h2>
          <p className="max-w-md font-sans text-sm text-content-muted">
            Una fase agrupa las sesiones de trabajo con la gente. Empezá por la
            primera: por ejemplo, «Escuchar a los vecinos sobre el agua en la
            zona 3» o «Priorizar el alumbrado con los COCODEs».
          </p>
          <FormNuevaFase procesoId={procesoId} etiquetaCta="Agregá la primera fase" grande />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-start gap-5 overflow-x-auto px-6 py-5">
          {arbol.fases.map((fase) => (
            <ColumnaFase
              key={fase.id}
              fase={fase}
              procesoId={procesoId}
              herramientaSeleccionada={herramientaSeleccionada}
              opcionesSesion={opcionesSesion}
            />
          ))}
          <FormNuevaFase procesoId={procesoId} etiquetaCta="Fase" />
        </div>
      )}
    </div>
  );
}

/**
 * «+ Fase» al final del lienzo: botón que abre un formulario inline con nombre
 * y tipo metodológico (una línea de microcopy por tipo explica la consecuencia
 * sobre la proyección).
 */
function FormNuevaFase({
  procesoId,
  etiquetaCta,
  grande = false,
}: {
  procesoId: string;
  etiquetaCta: string;
  grande?: boolean;
}) {
  const qc = useQueryClient();
  const grupoId = useId();
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoFase>('neutra');

  const crearMut = useMutation({
    mutationFn: () => composicionApi.crearFase(procesoId, { nombre: nombre.trim(), tipo }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) });
      setAbierto(false);
      setNombre('');
      setTipo('neutra');
    },
  });
  const accionError = (crearMut.error as ApiError | null)?.detail ?? null;

  if (!abierto) {
    return (
      <div className={grande ? '' : 'w-80 shrink-0'}>
        <Button
          variant={grande ? 'primary' : 'secondary'}
          size={grande ? 'lg' : 'sm'}
          fullWidth={!grande}
          onClick={() => setAbierto(true)}
          leadingIcon={<Plus className={grande ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden="true" />}
        >
          {etiquetaCta}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex w-80 shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface-raised px-4 py-4 text-left"
      onSubmit={(e) => {
        e.preventDefault();
        if (nombre.trim()) crearMut.mutate();
      }}
    >
      <Input
        label="Nombre de la fase"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder="Escuchar a los vecinos"
        autoFocus
        required
      />

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-sans text-sm font-medium text-content">
          Tipo de fase
        </legend>
        {TIPOS_FASE.map((t) => {
          const info = TIPO_FASE_INFO[t];
          return (
            <label
              key={t}
              className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 transition-colors duration-fast ${
                tipo === t ? 'border-primary bg-surface-sunken' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name={`${grupoId}-tipo`}
                value={t}
                checked={tipo === t}
                onChange={() => setTipo(t)}
                className="mt-1"
              />
              <span className="min-w-0">
                <span className="block font-sans text-sm font-semibold text-content">
                  {info.etiqueta}
                </span>
                <span className="block font-sans text-xs text-content-muted">
                  {info.micro}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <div className="flex items-center gap-2">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={crearMut.isPending}
          disabled={!nombre.trim()}
        >
          Crear fase
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setAbierto(false)}>
          Cancelar
        </Button>
      </div>

      {accionError && (
        <p className="flex items-start gap-1.5 font-sans text-xs text-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {accionError}
        </p>
      )}
    </form>
  );
}
